import { cloneElement, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { EmptyStateExtraSmall } from '@components/empty-state/empty-state';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconSearch } from '@components/icons/search';
import { IconTextAnonymize } from '@components/icons/text-anonymize';
import { ClampedText, useFieldTypeLabel } from '@components/integrations';
import { IntegrationEndpointVariable } from '@components/integrations/integrations-types';
import { TYPE_ICON_MAP } from '@components/integrations/styles';
import { Spinner } from '@components/loading-spinner';
import { hideModal, Modal, useModal } from '@components/modal';
import { SearchBox } from '@components/search-box';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useCustomFieldList } from 'api/app-marketplace/hooks';
import { CustomField } from 'api/app-marketplace/requests';
import {
  categoryColors,
  FieldTypeCategory,
} from 'ui/authenticated/settings/pages/attributes/common';

type FieldType = IntegrationEndpointVariable['type'];

// Which field-card category each integration type reads as — reusing the same
// inputs/selections split (and its colours) the attribute field cards use.
const TYPE_CATEGORY: Partial<Record<FieldType, FieldTypeCategory>> = {
  text: 'inputs',
  paragraph: 'inputs',
  number: 'inputs',
  decimal: 'inputs',
  date: 'inputs',
  email: 'inputs',
  phone: 'inputs',
  boolean: 'inputs',
  file: 'inputs',
  webhookUrl: 'inputs',
  secret: 'inputs',
  select: 'selections',
  array: 'objects',
  array_of_strings: 'objects',
};

const renderTypeIcon = (type: string) => {
  const category = TYPE_CATEGORY[type as FieldType];
  const icon = TYPE_ICON_MAP[type as FieldType];
  if (!category || !icon) {
    return <IconTextAnonymize color="elementPurple50" />;
  }
  return cloneElement(icon, { color: categoryColors[category] });
};

type AddCustomFieldModalProps = {
  integrationShortId: string;
  configShortId: string;
  parentContext: Record<string, string>;
  existingFieldIds: string[];
  onAdd: (field: CustomField) => void;
};

export const useAddCustomFieldModal = () => {
  return useModal(AddCustomFieldModal, { variant: 'REGULAR' });
};

const AddCustomFieldModal = ({
  integrationShortId,
  configShortId,
  parentContext,
  existingFieldIds,
  onAdd,
}: AddCustomFieldModalProps) => {
  const { t } = useScopedTranslate(locale);
  const fieldsQuery = useCustomFieldList(integrationShortId, configShortId, parentContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [addedFieldIds, setAddedFieldIds] = useState<string[]>([]);
  const hidden = new Set([...existingFieldIds, ...addedFieldIds]);

  const handleAdd = (field: CustomField) => {
    onAdd(field);
    setAddedFieldIds((prev) => [...prev, field.fieldId]);
  };

  const renderContent = () => {
    if (fieldsQuery.isLoading) {
      return (
        <SpinnerWrap>
          <Spinner />
        </SpinnerWrap>
      );
    }
    if (fieldsQuery.isError) {
      return (
        <Stack space={3} align="flex-start">
          <EmptyStateExtraSmall
            iconComponent={IconFormIndicatorWarning}
            title={t('loadFailedTitle')}
            description={t('loadFailedDescription')}
          />
          <Button text={t('retry')} variant="secondary" onClick={() => fieldsQuery.refetch()} />
        </Stack>
      );
    }
    const fields = fieldsQuery.data?.fields ?? [];
    const available = fields.filter((field) => !hidden.has(field.fieldId));
    const loweredQuery = searchQuery.trim().toLowerCase();
    const visible = loweredQuery
      ? available.filter((field) =>
          (field.name ?? field.fieldId).toLowerCase().includes(loweredQuery),
        )
      : available;

    if (available.length === 0) {
      if (fields.length === 0) {
        return (
          <EmptyStateExtraSmall
            iconComponent={IconSearch}
            title={t('noFieldsTitle')}
            description={t('noFieldsDescription')}
          />
        );
      }
      return (
        <EmptyStateExtraSmall
          iconComponent={IconCheckmark}
          title={t('allAddedTitle')}
          description={t('allAddedDescription')}
        />
      );
    }

    if (visible.length === 0) {
      return (
        <EmptyStateExtraSmall
          iconComponent={IconSearch}
          title={t('noResultsTitle')}
          description={t('noResultsDescription')}
        />
      );
    }

    return (
      <Stack space={2}>
        {visible.map((field) => (
          <FieldCard key={field.fieldId} field={field} onAdd={handleAdd} />
        ))}
      </Stack>
    );
  };

  return (
    <Modal>
      <Modal.SimpleHeader title={t('title')} />
      <Modal.Content>
        <Stack space={3}>
          <SearchBox
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t('searchPlaceholder')}
          />
          {renderContent()}
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" justify="flex-end">
          <Button text={t('close')} variant="secondary" onClick={hideModal} />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

const NameColumn = styled.div({
  flex: 1,
  minWidth: 0,
});

const SpinnerWrap = styled.div({
  display: 'flex',
  padding: pxToRem(48),
  alignItems: 'center',
  justifyContent: 'center',
});

const FieldTypeCard = styled(Card)({
  '&[data-category="inputs"]': { backgroundColor: theme.color.orangeTrans5 },
  '&[data-category="selections"]': { backgroundColor: theme.color.blueTrans5 },
  '&[data-category="objects"]': { backgroundColor: theme.color.indigoTrans5 },
});

type FieldCardProps = {
  field: CustomField;
  onAdd: (field: CustomField) => void;
};

const FieldCard = ({ field, onAdd }: FieldCardProps) => {
  const { t } = useScopedTranslate(locale);
  const fieldTypeLabel = useFieldTypeLabel();
  const isUnknownType = field.type === 'unknown';
  const baseLabel = fieldTypeLabel(field.type as FieldType);
  const typeLabel = isUnknownType
    ? t('typeNotSupported')
    : field.isArray
      ? t('arraySuffix', { label: baseLabel })
      : baseLabel;

  return (
    <FieldTypeCard
      space={3}
      variant={isUnknownType ? 'default' : 'transparent'}
      data-category={isUnknownType ? undefined : TYPE_CATEGORY[field.type as FieldType]}
    >
      <Stack orientation="horizontal" align="center" space={3}>
        {renderTypeIcon(field.type)}
        <NameColumn>
          <ClampedText font="semibold14" text={field.name} />
          <Text font="regular12" color="textHelper" text={typeLabel} />
        </NameColumn>
        {!isUnknownType && (
          <Button
            text={t('add')}
            variant="secondary"
            iconLeft={<IconAddPlus />}
            isDisabled={!field.template.trim()}
            data-testid={`add-field-${field.fieldId}`}
            onClick={() => {
              onAdd(field);
              hideModal();
            }}
          />
        )}
      </Stack>
    </FieldTypeCard>
  );
};

const locale = {
  en: {
    title: 'Add custom field',
    searchPlaceholder: 'Search fields...',
    close: 'Close',
    add: 'Add',
    typeNotSupported: 'Type not supported',
    noFieldsTitle: 'No available fields',
    noFieldsDescription: 'There are no custom fields available from this integration right now',
    allAddedTitle: 'All custom fields added',
    allAddedDescription: 'Every available field has already been added',
    noResultsTitle: 'No results found',
    noResultsDescription: 'Try adjusting your search',
    loadFailedTitle: "Couldn't load fields",
    loadFailedDescription:
      "Couldn't load fields from the integration — check the connection and try again.",
    retry: 'Try again',
    arraySuffix: '{label} (array)',
  },
};
